import React, { useState } from 'react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  XMarkIcon,
  ForwardIcon,
  BackwardIcon,
  ArrowDownTrayIcon,
  ArrowPathRoundedSquareIcon
} from '@heroicons/react/24/outline';

const FloatingAudioPlayer = () => {
  const { language } = useLanguage();
  const {
    isPlaying,
    currentAudio,
    currentTime,
    duration,
    volume,
    playbackSpeed,
    isLooping,
    togglePlay,
    clearAudio,
    seekTo,
    setVolume,
    toggleLoop,
    changePlaybackSpeed
  } = useAudioPlayer();
  
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);

  if (!currentAudio) return null;

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 p-4 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Audio Info */}
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-600 rounded-lg">
            <SpeakerWaveIcon className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-white font-medium truncate">
              {currentAudio.title || (language === 'ar' ? 'المحتوى الصوتي' : 'Audio Content')}
            </h4>
            <p className="text-slate-400 text-sm">
              {language === 'ar' ? 'جاري التشغيل' : 'Now Playing'}
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-6">
          {/* Seek backward */}
          <button
            onClick={() => seekTo(Math.max(0, currentTime - 10))}
            className="text-slate-400 hover:text-white transition-colors"
            title={language === 'ar' ? 'الرجوع 10 ثواني' : 'Back 10 seconds'}
          >
            <BackwardIcon className="h-5 w-5" />
          </button>

          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className="flex items-center justify-center w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors"
          >
            {isPlaying ? (
              <PauseIcon className="h-6 w-6 text-white" />
            ) : (
              <PlayIcon className="h-6 w-6 text-white ml-0.5" />
            )}
          </button>

          {/* Seek forward */}
          <button
            onClick={() => seekTo(Math.min(duration, currentTime + 10))}
            className="text-slate-400 hover:text-white transition-colors"
            title={language === 'ar' ? 'التقدم 10 ثواني' : 'Forward 10 seconds'}
          >
            <ForwardIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Progress and Time */}
        <div className="flex items-center space-x-4 flex-1 min-w-0 ml-6">
          <span className="text-slate-400 text-sm whitespace-nowrap">
            {formatTime(currentTime)}
          </span>
          
          {/* Progress Bar */}
          <div className="flex-1 relative">
            <div className="h-2 bg-slate-700 rounded-full cursor-pointer"
                 onClick={(e) => {
                   const rect = e.currentTarget.getBoundingClientRect();
                   const x = e.clientX - rect.left;
                   const percentage = x / rect.width;
                   seekTo(duration * percentage);
                 }}>
              <div 
                className="h-2 bg-blue-600 rounded-full relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-blue-600 rounded-full shadow-lg"></div>
              </div>
            </div>
          </div>
          
          <span className="text-slate-400 text-sm whitespace-nowrap">
            {formatTime(duration)}
          </span>
        </div>

        {/* Volume Control */}
        <div className="flex items-center space-x-2 ml-6">
          <SpeakerWaveIcon className="h-4 w-4 text-slate-400" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-20 h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* Playback Speed Button */}
        <div className="relative ml-4">
          <button
            onClick={() => setShowSpeedOptions(!showSpeedOptions)}
            className={`text-slate-400 hover:text-white transition-colors px-2 py-1 rounded ${showSpeedOptions ? 'bg-slate-700' : ''}`}
            title={language === 'ar' ? 'سرعة التشغيل' : 'Playback Speed'}
          >
            <span className="text-xs font-mono">{playbackSpeed}x</span>
          </button>
          
          {showSpeedOptions && (
            <div className="absolute bottom-full left-0 mb-2 bg-slate-800 rounded shadow-lg border border-slate-700 z-10">
              {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                <button
                  key={speed}
                  onClick={() => {
                    changePlaybackSpeed(speed);
                    setShowSpeedOptions(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-xs ${
                    playbackSpeed === speed ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Loop Button */}
        <button
          onClick={toggleLoop}
          className={`ml-3 ${isLooping ? 'text-blue-400' : 'text-slate-400 hover:text-white'} transition-colors`}
          title={language === 'ar' ? 'تكرار' : 'Loop'}
        >
          <ArrowPathRoundedSquareIcon className="h-5 w-5" />
        </button>
        
        {/* Download Button */}
        <a
          href={currentAudio?.url}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="ml-3 text-slate-400 hover:text-white transition-colors"
          title={language === 'ar' ? 'تحميل' : 'Download'}
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
        </a>

        {/* Close Button */}
        <button
          onClick={clearAudio}
          className="ml-4 text-slate-400 hover:text-white transition-colors"
          title={language === 'ar' ? 'إغلاق' : 'Close'}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default FloatingAudioPlayer;
